import { z } from 'zod'

import { DestinationLocalHandlingProductCode, OriginLocalHandlingProductCode } from '@forto-dev/shipment-models'
import { CustomerHouseShipmentResponse as CustomerHouseShipmentResponseType } from '@forto-dev/shipment-models/dist/houseShipmentModels/customer/customerHouseShipment.response'
import { milestoneDateTypeValues } from '@forto-dev/shipment-models/dist/models-shared/milestoneDate.type'

import { SHIPMENT_STATES } from './shipment'
import { TRANSPORT_MODES } from './transportMode'
import { TRANSPORT_TYPES } from './transportType'

export type CustomerHouseShipmentResponse = CustomerHouseShipmentResponseType

const TAG_KEYS = ['ShipperReference', 'ConsigneeReference', 'PurchaseOrderNumber', 'BookingReference'] as const
const NODE_TYPES = [
  'depot',
  'place-of-pickup',
  'customs-stop',
  'multi-stop',
  'vgm',
  'terminal',
  'place-of-delivery',
  'port-of-loading',
  'port-of-discharge',
  'transshipment-port',
] as const
const TIMESTAMP_TYPES = ['date', 'timestamp', 'timestamp-interval'] as const
const SHIPMENT_ADDRESS_TYPES = [
  'origin',
  'destination',
  'pickup',
  'delivery',
  'portOfLoading',
  'portOfDestination',
] as const

const HOUSE_CARGO_MILESTONES = [
  'cargoReadyDate',
  'emptyContainerPickup',
  'pickUpDate',
  'cargoGateIn',
  'timeOfDeparture',
  'timeOfArrival',
  'cargoGateOut',
  'timeOfDelivery',
  'emptyContainerDropOff',
  'cargoLoadedAtPort',
  'cargoUnloadedAtPort',
  'containerDepartureFromConsignee',
  'cargoDepartureFromShipper',
  'customsClearanceReleased',
  'containerGateInMultimodalOrigin',
  'containerGateInMultimodalDestination',
  'containerGateOutMultimodalOrigin',
  'containerGateOutMultimodalDestination',
  'containerVGMVerified',
  'requestedPickupAt',
  'requestedDeliveryAt',
] as const

export type HouseCargoMilestones = (typeof HOUSE_CARGO_MILESTONES)[number]

const HouseCargoMilestoneValueSchema = z
  .object({
    date: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    isActual: z.boolean(),
  })
  .nullish()
  .transform((data) => data ?? null)

// An object, not a record, so milestone keys Ship does not know yet are stripped instead of failing the parse
const houseCargoMilestonesShape = Object.fromEntries(
  HOUSE_CARGO_MILESTONES.map((milestone) => [milestone, HouseCargoMilestoneValueSchema])
) as Record<HouseCargoMilestones, typeof HouseCargoMilestoneValueSchema>

const HouseCargoMilestonesObjectSchema = z.object(houseCargoMilestonesShape)

type HouseCargoMilestonesMap = Partial<Record<HouseCargoMilestones, z.infer<typeof HouseCargoMilestoneValueSchema>>>

export const HouseShipmentMilestoneDateSchema = z.object({
  timeZone: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  timeStampUTC: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  endTimeStampUTC: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  type: z.enum([...milestoneDateTypeValues, ...TIMESTAMP_TYPES] as unknown as [string, ...string[]]).or(z.string()),
  isActual: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  reason: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  delayInDays: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
})

export const HouseShipmentAddressSchema = z.object({
  type: z
    .enum(SHIPMENT_ADDRESS_TYPES)
    .nullish()
    .transform((data) => data ?? undefined),
  addressID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  types: z.array(z.string()),
  location: z.object({
    latitude: z
      .number()
      .nullish()
      .transform((data) => data ?? undefined),
    longitude: z
      .number()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
  companyName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  street: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  street2: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  streetNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  city: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  district: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  state: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  country: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  countryCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  postalCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  googlePlace: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type HouseShipmentAddress = z.infer<typeof HouseShipmentAddressSchema>

export const HouseShipmentNodeMilestonesSchema = z.object({
  timeOfArrival: HouseShipmentMilestoneDateSchema.nullish().transform((data) => data ?? undefined),
  timeOfDeparture: HouseShipmentMilestoneDateSchema.nullish().transform((data) => data ?? undefined),
})

export type HouseShipmentNodeMilestones = z.infer<typeof HouseShipmentNodeMilestonesSchema>

export const HouseShipmentTransportPointSchema = z.object({
  address: HouseShipmentAddressSchema.nullish().transform((data) => data ?? undefined),
  // Master data locations arrive as an ID with address left null, so both shapes have to be read.
  locationID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  port: z
    .object({
      ID: z.string(),
      name: z.string(),
      countryCode: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
  depot: z
    .object({ ID: z.string(), name: z.string() })
    .nullish()
    .transform((data) => data ?? undefined),
  terminal: z
    .object({ ID: z.string(), name: z.string() })
    .nullish()
    .transform((data) => data ?? undefined),
})

export type HouseShipmentTransportPoint = z.infer<typeof HouseShipmentTransportPointSchema>

export const HouseShipmentNodeSchema = z.object({
  id: z.string(),
  type: z.enum(NODE_TYPES).or(z.string()),
  delayInDays: z
    .number()
    .nullish()
    .transform((data) => data ?? 0),
  instructions: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  milestones: HouseShipmentNodeMilestonesSchema,
  transportPoint: HouseShipmentTransportPointSchema,
  outgoingConnections: z
    .array(
      z.object({
        houseCargoIDs: z.array(z.string()),
        modeOfTransport: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        targetNodeID: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        vendor: z
          .object({
            accountID: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
            legalEntityID: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
            partnerCode: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
          })
          .nullish()
          .transform((data) => data ?? undefined),
        vesselName: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        flightNumber: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
    )
    .nullish()
    .transform((data) => data ?? []),
})

export type HouseShipmentNode = z.infer<typeof HouseShipmentNodeSchema>

export const CargoItemSchema = z.object({
  id: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  createdAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  updatedAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  goodDescription: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  hsCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  marksAndNumbers: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  numberOfPackages: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  weightInKg: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  grossWeightInKg: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  netWeightInKg: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  packageDimensions: z
    .object({
      lengthInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      heightInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      widthInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      volumeInCubicMeters: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
  packageType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  purchaseOrderNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  purchaseOrderPosition: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  skuNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportUnitCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type CargoItem = z.infer<typeof CargoItemSchema>

export const HouseCargoSchema = z.object({
  ID: z.string(),
  isStackable: z
    .boolean()
    .nullish()
    .transform((data) => data ?? false),
  containerNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  sealNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportUnitCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  cargoGrossWeightInKg: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  customerReference: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityDescription: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  dimensions: z
    .object({
      lengthInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      heightInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      widthInCM: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      volumeInCubicMeters: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
  milestones: HouseCargoMilestonesObjectSchema.nullish().transform((data): HouseCargoMilestonesMap => data ?? {}),
  cargoItems: z.array(CargoItemSchema),
  unNumbers: z
    .array(z.string())
    .nullish()
    .transform((data) => data ?? []),
  commodities: z
    .array(
      z.object({
        id: z
          .string()
          .nullish()
          .transform((data) => data ?? null),
        type: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        description: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
    )
    .nullish()
    .transform((data) => data ?? []),
})

export type HouseCargo = z.infer<typeof HouseCargoSchema>

export const HouseCargosSchema = z.array(HouseCargoSchema).default([])

export const HouseShipmentTagSchema = z.object({ key: z.enum(TAG_KEYS), value: z.string() })

export type HouseShipmentTag = z.infer<typeof HouseShipmentTagSchema>

export const HouseShipmentDetailsSchema = z.object({
  mainTransportType: z.enum(TRANSPORT_TYPES).or(z.string()),
  mainTransportMode: z.enum(TRANSPORT_MODES).or(z.string()),
  additionalInformation: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  customerReference: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookedAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingConfirmationRecipients: z
    .array(z.string())
    .nullish()
    .transform((data) => data ?? undefined),
  shipperLegalEntityID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  consigneeLegalEntityID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  onBehalfOfShipperLegalEntityID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  onBehalfOfConsigneeLegalEntityID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  houseBillOfLadings: z
    .array(
      z.object({
        type: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        number: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
    )
    .default([]),
  incoterms: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  tags: z
    .array(z.any())
    .default([])
    .transform((tags) =>
      tags
        .filter((tag) => {
          const result = HouseShipmentTagSchema.safeParse(tag)
          return result.success
        })
        .map((tag) => HouseShipmentTagSchema.parse(tag))
    ),
  tradeClass: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  cargoReadyDate: HouseShipmentMilestoneDateSchema.nullish().transform((data) => data ?? undefined),
  // .catch(undefined): requested dates are display-only, an unexpected payload
  // shape must not fail the whole shipment parse (e.g. environments running an
  // older shipments service).
  requestedEtd: HouseShipmentMilestoneDateSchema.nullish()
    .transform((data) => data ?? undefined)
    .catch(undefined),
  requestedEta: HouseShipmentMilestoneDateSchema.nullish()
    .transform((data) => data ?? undefined)
    .catch(undefined),
  requestedPickupAt: HouseShipmentMilestoneDateSchema.nullish().transform((data) => data ?? undefined),
  requestedDeliveryAt: HouseShipmentMilestoneDateSchema.nullish().transform((data) => data ?? undefined),
  airline: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  serviceLevel: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  flightNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  chargeableWeightKG: z
    .number()
    .nullish()
    .transform((data) => data ?? null),
  houseAirwayBillOfLadingNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type HouseShipmentDetails = z.infer<typeof HouseShipmentDetailsSchema>

const LocalHandlingProductSchema = z.object({
  code: z.union([z.nativeEnum(OriginLocalHandlingProductCode), z.nativeEnum(DestinationLocalHandlingProductCode)]),
  name: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

const LocalHandlingServiceSchema = z.object({
  products: z
    .array(LocalHandlingProductSchema)
    .nullish()
    .transform((data) => data ?? []),
})

export const HouseShipmentSchema = z.object({
  code: z.string(),
  v2Flow: z.boolean().default(false),
  houseCargos: HouseCargosSchema,
  status: z.enum(SHIPMENT_STATES),
  customerShipmentStatus: z
    .object({
      status: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      delayInDays: z
        .number()
        .nullish()
        .transform((data) => data ?? undefined),
      delayBasis: z
        .enum(['arrival', 'delivery'])
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
  details: HouseShipmentDetailsSchema.default({ mainTransportType: 'fcl', mainTransportMode: 'sea', tags: [] }),
  customsDetails: z.object({
    originCustomsType: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    destinationCustomsType: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
  nodes: z.array(HouseShipmentNodeSchema).default([]),
  contacts: z.object({
    shipperContactID: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    shipperContactName: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    consigneeContactID: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    consigneeContactName: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    onBehalfOfShipperContactID: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    onBehalfOfConsigneeContactID: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    consigneeContact: z
      .object({
        address: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        companyName: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        contactPerson: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        email: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        externalId: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        phone: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
      .nullish()
      .transform((data) => data ?? undefined),
    shipperContact: z
      .object({
        address: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        companyName: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        contactPerson: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        email: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        externalId: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        phone: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
      .nullish()
      .transform((data) => data ?? undefined),
  }),
  trackingProvider: z.string().default('transport-plan'),
  sys: z.object({
    createdAt: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined),
    source: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
  services: z
    .object({
      airFreightService: z
        .object({
          ID: z.string(),
          serviceID: z.string(),
          serviceType: z.string(),
          houseCargoIDs: z
            .array(z.string())
            .nullish()
            .transform((data) => data ?? []),
        })
        .nullish()
        .transform((data) => data ?? undefined),
      insuranceService: z
        .object({
          commodities: z.array(z.object({ class: z.string(), goodsValue: z.number(), currencyCode: z.string() })),
        })
        .nullish()
        .transform((data) => data ?? undefined),
      buyersConsolidationService: z
        .object({})
        .nullish()
        .transform((data) => data ?? undefined),
      dangerousGoodsService: z
        .object({})
        .nullish()
        .transform((data) => data ?? undefined),
      specialEquipmentService: z
        .object({})
        .nullish()
        .transform((data) => data ?? undefined),
      sustainabilityService: z
        .object({
          products: z
            .array(
              z.object({
                serviceType: z
                  .string()
                  .nullish()
                  .transform((data) => data ?? undefined),
                selectedProduct: z
                  .string()
                  .nullish()
                  .transform((data) => data ?? undefined),
                transportMode: z
                  .string()
                  .nullish()
                  .transform((data) => data ?? undefined),
              })
            )
            .nullish()
            .transform((data) => data ?? undefined),
        })
        .nullish()
        .transform((data) => data ?? undefined),
      exportCustomsService: z
        .any()
        .nullish()
        .transform((data) => data ?? undefined),
      importCustomsService: z
        .any()
        .nullish()
        .transform((data) => data ?? undefined),

      originLocalHandlingService: LocalHandlingServiceSchema.nullish().transform((data) => data ?? undefined),
      destinationLocalHandlingService: LocalHandlingServiceSchema.nullish().transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
})

export type HouseShipment = z.infer<typeof HouseShipmentSchema>
